import { useMemoizedFn } from "ahooks";
import { useCallback, useEffect, useRef, useState } from "react";
import throttle from "lodash-es/throttle";

interface ScrollManagerOptions {
  topOffset?: number;
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  disabled?: boolean;
}

export function useScrollManager(options: ScrollManagerOptions = {}) {
  const initDisabled =
    typeof options.disabled !== "undefined" ? options.disabled : true;
  const { topOffset = 0, scrollContainerRef } = options;
  const [disabled, setdisabled] = useState(initDisabled);
  const isScrollingUpRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const targetScrollHeightRef = useRef(0);
  const lastScrollHeightRef = useRef(0); // 记录上次的滚动高度
  const hasAutoPositionedRef = useRef(false);

  const containerRef = useRef<HTMLElement | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const rafIdRef = useRef<number | null>(null); // 用于 requestAnimationFrame

  // 滚动到底部的函数
  const scrollToBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container || isScrollingUpRef.current || disabled) {
      return;
    }

    // 使用容器的scrollHeight和clientHeight计算滚动位置
    const scrollHeight = container.scrollHeight - container.clientHeight;

    // 优化：只有当高度真正变化时才滚动，避免无意义的滚动操作
    // 允许1px的误差，因为浏览器渲染可能存在亚像素差异
    if (Math.abs(scrollHeight - lastScrollHeightRef.current) < 1) {
      return;
    }

    // 更新记录的高度
    lastScrollHeightRef.current = scrollHeight;
    targetScrollHeightRef.current = scrollHeight;

    const behavior = hasAutoPositionedRef.current ? "smooth" : "auto";
    hasAutoPositionedRef.current = true;

    // 使用scrollTo方法并添加平滑滚动效果
    container.scrollTo({
      top: scrollHeight - topOffset,
      behavior,
    });
  }, [topOffset, disabled]);

  // 处理容器大小变化
  const handleResize = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  // 处理滚动事件
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const currentScrollTop = container.scrollTop;
    const maxScrollTop = container.scrollHeight - container.clientHeight;

    // 检测用户是否正在向上滚动
    if (
      currentScrollTop < maxScrollTop &&
      currentScrollTop < lastScrollTopRef.current
    ) {
      isScrollingUpRef.current = true;
    } else if (Math.abs(currentScrollTop - maxScrollTop) < 5) {
      // 如果接近底部（允许5px的误差），认为用户已回到底部
      isScrollingUpRef.current = false;
    }

    lastScrollTopRef.current = currentScrollTop;
  }, []);

  // 监听DOM变化的函数
  const observeContentChanges = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    // 如果已经有MutationObserver，先断开连接
    if (mutationObserverRef.current) {
      mutationObserverRef.current.disconnect();
    }

    let pendingUpdate = false;

    // 创建新的MutationObserver来监听内容变化
    const observer = new MutationObserver(() => {
      // 使用 requestAnimationFrame 节流，避免频繁执行
      // 这样可以将多次 DOM 变化合并到一次滚动操作中
      if (pendingUpdate) {
        return;
      }

      pendingUpdate = true;
      rafIdRef.current = requestAnimationFrame(() => {
        scrollToBottom();
        pendingUpdate = false;
        rafIdRef.current = null;
      });
    });

    // 监听子节点和子树的变化
    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    mutationObserverRef.current = observer;
  }, [scrollToBottom]);

  useEffect(() => {
    // 获取容器元素
    const container =
      scrollContainerRef?.current || document.getElementById("container");
    if (!container) {
      return;
    }

    containerRef.current = container;

    // 设置ResizeObserver监听容器大小变化
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    // 使用节流处理滚动事件
    const throttledHandleScroll = throttle(handleScroll, 150, {
      leading: true,
      trailing: true,
    }); // 每 150ms 最多执行一次

    // 监听容器的滚动事件，而不是window的滚动事件
    container.addEventListener("wheel", throttledHandleScroll, {
      passive: true,
    });
    container.addEventListener("touchstart", throttledHandleScroll, {
      passive: true,
    });
    container.addEventListener("touchmove", throttledHandleScroll, {
      passive: true,
    });

    // 监听内容变化
    observeContentChanges();

    // 初始滚动到底部
    scrollToBottom();

    return () => {
      container.removeEventListener("wheel", throttledHandleScroll);
      container.removeEventListener("touchstart", throttledHandleScroll);
      container.removeEventListener("touchmove", throttledHandleScroll);

      // 取消未完成的节流调用
      throttledHandleScroll.cancel();

      resizeObserver.disconnect();

      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }

      // 清理 requestAnimationFrame
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [
    handleResize,
    handleScroll,
    observeContentChanges,
    scrollToBottom,
    scrollContainerRef,
  ]);

  return {
    isScrollingUpRef,
    setIsScrollingUp: useMemoizedFn((value: boolean) => {
      isScrollingUpRef.current = value;
    }),
    scrollToBottom, // 导出滚动到底部的方法，以便外部可以主动调用
    setdisabled,
    disabled,
  };
}
